import {
  Table,
  TableCaption,
  TableColumnHeaderProps,
  TableContainer,
  TableContainerProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import {
  Children,
  PropsWithChildren,
  ReactElement,
  isValidElement
} from 'react';
import { PFTr } from '../../atoms/data-display/table/payflip-tr';
import { PageChanger } from '../page-changer/page-changer';
import { PFTableStyle, newPFTableStyle } from './style';

interface RecognizedComponents {
  caption: ReactElement | null;
  header: ReactElement[];
  rows: ReactElement[];
  pager: ReactElement | null;
}

function findRecognizedComponents(
  children: React.ReactNode
): RecognizedComponents {
  const result: RecognizedComponents = {
    caption: null,
    header: [],
    rows: [],
    pager: null
  };

  const searchChildren = (
    child: JSX.Element
  ): RecognizedComponents | undefined | void => {
    if (!isValidElement(child)) {
      return;
    }

    if (child.type === TableCaption) {
      result.caption = child;
    } else if (
      child.type === Tr ||
      (child as JSX.Element).type.displayName === 'tableHeader'
    ) {
      result.header.push(child);
    } else if (child.type === PFTr) {
      result.rows.push(child);
    } else if (child.type === PageChanger) {
      result.pager = child;
    }
  };

  Children.forEach(children as ReactElement, searchChildren);
  return result;
}

export function PFTable({
  children,
  hasNoBorderDesign = false,
  hasSpaceAfterHeader = false,
  ...props
}: PropsWithChildren<
  TableContainerProps & {
    hasNoBorderDesign?: boolean;
    hasSpaceAfterHeader?: boolean;
  }
>): JSX.Element {
  const { caption, header, rows, pager } = findRecognizedComponents(children);

  return (
    <TableContainer {...props} w="100%" overflowX="auto">
      <Table
        w="100%"
        variant="unstyled"
        sx={hasNoBorderDesign ? newPFTableStyle : PFTableStyle}
      >
        {caption}
        <Thead>{header}</Thead>
        <Tbody
          _before={
            hasSpaceAfterHeader
              ? {
                  lineHeight: '0.1rem',
                  content: "'\u200C'",
                  display: 'block'
                }
              : {}
          }
        >
          {rows}
        </Tbody>
      </Table>
      {pager}
    </TableContainer>
  );
}

PFTable.Caption = TableCaption;
PFTable.Row = PFTr;
PFTable.Cell = Td;
PFTable.Header = Tr;
PFTable.HeaderCell = (props: TableColumnHeaderProps): JSX.Element => (
  <Th textTransform="inherit" textAlign={'start'} {...props} />
);
PFTable.PageChanger = PageChanger;
